import { put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { AnyAction } from "typescript-fsa";

import {
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { TASK_DETAIL_ACTIVITY_ID } from "../../modules/processOverview/store/actions";
import { findAnnotations } from "../../utilities/modelHelpers";
import { INPUT_ANTRAGSDATUM, OUTPUT_ANTRAGSDATUM } from "../../utilities/variables/modulfRuntimeAnnotations";
import { createZeroIndexedEntityInstancePath } from "../../utilities/pathHelpers";

import { createPath, createStringValidationPath, ensureParent } from "./datumValidationSaga";

/**
 * A saga that prepares data for a server side date validations that depends on antransdatum.
 * When Antrasdatum is imported by Datei Einlesen AFM or XML Einlesen modules,
 * it reads antragsdatum value and copies it to every module, that has a special annotation.
 */

export function* datumValidationAFMSaga(): SagaGenerator<void> {
	yield* takeLatest(
		(action: AnyAction) => ActivityActions.setData.match(action),
		function* (action: ReturnType<typeof FormEngineActions.event>) {
			const activityId = action.payload.activityId;
			if (activityId === TASK_DETAIL_ACTIVITY_ID) {
				const activity = yield* select(ActivitySelectors.activityById(activityId));
				if (!activity) {
					console.error(`Activity ${activityId} not found.`);
					return;
				}

				const engineState = yield* select(FormEngineSelectors.engineState(activityId));
				if (!engineState) {
					console.error("Couldn't get EngineState");
					return;
				}

				const documentModel = ModelSelectors.documentModel()(engineState);
				const antragAnnotations = findAnnotations(documentModel, INPUT_ANTRAGSDATUM);
				if (antragAnnotations.size > 0) {
					const documentService = new DocumentServiceFactory().getDocumentService();
					const data = yield* select(ActivitySelectors.data(activityId)) as any;

					const documentUpdateResult = Array.from(antragAnnotations.entries())
						.map(([paths, _]) => paths)
						.reduce(
							(documentAcc, antragsdatumAnnotationPaths) => {
								antragsdatumAnnotationPaths.shift(); //remove RootGroup element
								const antragsdatumTarget = antragsdatumAnnotationPaths.pop()!;
								const antragEntityInstancePath = createZeroIndexedEntityInstancePath(
									createStringValidationPath(antragsdatumAnnotationPaths)
								);
								const antragsdatumPath = createPath(
									documentService,
									documentAcc[1],
									antragEntityInstancePath,
									antragsdatumTarget
								);
								const antragsdatum = documentService.getAssignedObject(
									documentAcc[1],
									antragsdatumPath
								);
								const dateAnnotations = findAnnotations(documentModel, OUTPUT_ANTRAGSDATUM);
								if (dateAnnotations.size > 0) {
									const innerDocumentUpdateResult = Array.from(dateAnnotations.entries())
										.map(([paths, _]) => paths)
										.reduce(
											(innerDocumentAcc, annotationPaths) => {
												annotationPaths.shift(); //remove RootGroup element
												const target = annotationPaths.pop()!;
												const parentEntityInstancePath = createZeroIndexedEntityInstancePath(
													createStringValidationPath(annotationPaths)
												);
												const ensuredDocument = ensureParent(
													innerDocumentAcc[1],
													[...annotationPaths],
													parentEntityInstancePath,
													documentService,
													documentModel
												);
												if (ensuredDocument) {
													const path = createPath(
														documentService,
														ensuredDocument,
														parentEntityInstancePath,
														target
													);
													const datum = documentService.getAssignedObject(
														innerDocumentAcc[1],
														path
													);
													if ((!datum && !antragsdatum) || datum === antragsdatum) {
														return [innerDocumentAcc[0] || false, innerDocumentAcc[1]];
													}
													return [
														innerDocumentAcc[0] || true,
														documentService.updateEntityInstance(
															ensuredDocument,
															path,
															antragsdatum,
															documentModel
														)
													];
												}
												return [innerDocumentAcc[0] || false, innerDocumentAcc[1]];
											},
											[documentAcc[0], documentAcc[1]]
										);
									return [
										innerDocumentUpdateResult[0] || documentAcc[0],
										innerDocumentUpdateResult[1]
									];
								}
								return [documentAcc[0] || false, documentAcc[1]];
							},
							[false, data?.document]
						);
					if (documentUpdateResult[0]) {
						yield* put(
							ActivityActions.setData({
								activityId,
								data: { document: documentUpdateResult[1] }
							})
						);
					}
				}
			}
		}
	);
}
